import cn from 'classnames';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { isDesktop, isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import GameCard from 'src/components/common/GameCard';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { Typography } from 'src/components/common/Typography';
import { icons } from 'src/configs/icons';
import { RootState } from 'src/types/store-types';
import { GAME_CARD_SIZES, SPECIAL_TAGS, TEMPLATES_WITH_DETAILS } from 'src/utils/constants';
import './styles.scss';

type GameCardPageProps = {
  tagGames: any;
  gamesTotalCount: number;
  changeVisibleGamesCount: (arg: boolean) => void;
  gamesMinVisibleCount: number;
};
const GameCardPage: FC<GameCardPageProps> = ({
  tagGames,
  changeVisibleGamesCount,
  gamesMinVisibleCount,
  gamesTotalCount,
}) => {
  const { t }: Translation = useTranslation();
  const gameContainerRef = useRef<HTMLDivElement>(null);

  const { generalConfigs } = useSelector((state: RootState) => state.configs);
  const { selectedTag } = useSelector((state: RootState) => state.games);
  const { sideBarMenuIsOpen } = useSelector((state: RootState) => state.app);

  const [gamesCountInARow, setGamesCountInARow] = useState<number>(0);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef<boolean>(false);
  const lastRequestedLengthRef = useRef<number>(-1);

  const getScrollParent = (node: HTMLElement | null): HTMLElement | null => {
    let current = node?.parentElement ?? null;
    while (current) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') {
        return current;
      }
      current = current.parentElement;
    }
    return null;
  };

  const scrollToMiddle = (): void => {
    const container = getScrollParent(gameContainerRef.current);
    if (container) {
      const maxScroll = container.scrollHeight - container.clientHeight;
      const top = Math.max(0, Math.floor(maxScroll * 0.9));
      container.scrollTo({ top, behavior: 'smooth' });
      return;
    }

    const doc = document.documentElement;
    const maxScroll = doc.scrollHeight - window.innerHeight;
    const top = Math.max(0, Math.floor(maxScroll * 0.9));
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const showDetails = useMemo(() => {
    return TEMPLATES_WITH_DETAILS.includes(generalConfigs?.template);
  }, []);

  useEffect(() => {
    !!gameContainerRef?.current?.clientWidth &&
      setGamesCountInARow(
        (gameContainerRef?.current?.clientWidth as number) / (GAME_CARD_SIZES[isMobileOnly ? 'sm' : 'md_l'] + 10)
      );
  }, [gameContainerRef?.current?.clientWidth, sideBarMenuIsOpen]);

  useEffect(() => {
    isLoadingMoreRef.current = false;
    lastRequestedLengthRef.current = -1;
  }, [tagGames?.length]);

  useEffect(() => {
    if (SPECIAL_TAGS.includes(selectedTag)) return;
    if (!loadMoreRef.current) return;
    if (!tagGames?.length) return;
    if (tagGames.length < gamesMinVisibleCount) return;
    if (gamesTotalCount <= tagGames.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry.isIntersecting || isLoadingMoreRef.current) return;
        if (lastRequestedLengthRef.current === (tagGames?.length ?? 0)) return;
        isLoadingMoreRef.current = true;
        lastRequestedLengthRef.current = tagGames?.length ?? 0;
        setTimeout(() => {
          changeVisibleGamesCount(true);
          scrollToMiddle();
        }, 500);
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [changeVisibleGamesCount, gamesMinVisibleCount, gamesTotalCount, selectedTag, tagGames?.length]);

  if (!tagGames?.length && tagGames !== null) {
    return (
      <div className="games_no_data_wrapper">
        <SvgIcon src={icons.noResult} />
        <Typography className="text" variant={'h4'}>
          {t('no_results_found')}
        </Typography>
      </div>
    );
  }

  return (
    <div className="games_wrapper">
      <div
        className={cn(
          'games_container',
          generalConfigs?.template ?? 'template_noFullScreen',
          showDetails ? 'has-details' : ''
        )}
        style={{
          gridTemplateColumns: isMobileOnly ? `repeat(2, 1fr)` : `repeat(${Math.floor(gamesCountInARow)}, 1fr)`,
        }}
        ref={gameContainerRef}
      >
        {!!tagGames &&
          tagGames?.map((game: GameDataType, i: number) => {
            return (
              <GameCard
                key={game.name + i.toString()}
                data={game}
                showDetails={showDetails}
                effects={{
                  hoverType: isDesktop ? 'fullWidthImage' : '',
                  src: '',
                  text: '',
                }}
                useGif={generalConfigs?.useGifAsGameLoader}
                size={isMobileOnly ? 'sm' : 'md_l'}
              />
            );
          })}
      </div>
      {!SPECIAL_TAGS.includes(selectedTag) &&
        gamesTotalCount > tagGames?.length &&
        tagGames?.length >= gamesMinVisibleCount && <div ref={loadMoreRef} />}
      {/*{gamesTotalCount === tagGames?.length && tagGames?.length > gamesMinVisibleCount && (
        <Button className="show_more_btn" onClick={() => changeVisibleGamesCount(false)}>
          {t('button_show_less')}
        </Button>
      )}*/}
    </div>
  );
};

export default GameCardPage;
